import React, {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationCircle, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {useLocation, useNavigate} from "react-router-dom";
import {defaultFormState} from "../../../../defaults/Forms";
import {locationDefault} from "../../../../defaults/Items";
import {
  compareData,
  onCloseDetails,
  onItemsListDeleteButtonClick,
  onSaveAndClose,
  restoreFormData
} from "../../../../helpers/ComponentHelper";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import SettingsEntityModal from "../../common/SettingsEntityModal";
import TextInput from "../../../elements/TextInput";
import TextArea from "../../../elements/TextArea";
import useCrud from "../../../../hooks/useCrud";
import {Table} from "../../../table/Table";

const Location = () => {
  const dataUrl = "/locations";
  const defaultItem = locationDefault;
  const itemName = "lokalizacja";
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const {createItem, updateItem, deleteItem, getItems} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Skrócona nazwa", accessor: "shortName", sortable: true, searchable: true},
    {label: "Opis", accessor: "description", sortable: true, searchable: true},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}
  ];

  const state = {
    itemsList, setItems,
    currentItem, setCurrentItem,
    currentFormState, setCurrentFormState,
    defaultItem, backupItem, setBackupItem,
    itemChanged, setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  };

  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403) {
      navigate("/login", {state: {from: routerLocation}, replace: true});
    }
  };

  const onSaveItemClick = async event => {
    event.preventDefault();
    if (!currentItem.name?.trim() || !currentItem.shortName?.trim()) {
      if (!currentItem.name?.trim()) document.getElementById("name")?.classList.add("form-input-invalid");
      if (!currentItem.shortName?.trim()) document.getElementById("shortName")?.classList.add("form-input-invalid");
      return;
    }

    const item = {
      id: currentItem.id,
      name: currentItem.name.trim(),
      shortName: currentItem.shortName.trim(),
      description: currentItem.description?.trim() || null
    };
    const response = currentFormState.formAddingDataMode
      ? await createItem(dataUrl, item, state)
      : await updateItem(`${dataUrl}/${item.id}`, item, state);

    if (response?.status === 401 || response?.status === 403) {
      navigate("/login", {state: {from: routerLocation}, replace: true});
    } else if (response?.status === 409) {
      setUpdateConflict(true);
    } else if (response) {
      onSaveAndClose({state});
    }
  };

  const onClose = () => {
    setUpdateConflict(false);
    onCloseDetails({state});
  };

  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged]);

  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.name);
    }
  }, [allowDelete]);

  useEffect(() => {
    const load = async () => {
      const response = await getItems(dataUrl);
      if (response.status === 200) {
        setItems(response.data || []);
        setLoading(false);
      } else if (response.status === 401 || response.status === 403) {
        navigate("/login", {state: {from: routerLocation}, replace: true});
      }
    };
    load();
  }, []);

  const getCompleteItem = async id => {
    const response = await getItems(`${dataUrl}/${id}`);
    return response.status === 200 ? response.data : null;
  };

  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: "",
    formHeader: "Nowa lokalizacja",
    buttonTitle: "Dodaj lokalizację"
  };

  return (
    <div id="layoutSidenav_content" className="settings-entity-page">
      <AppComponentCardHeader title="Lokalizacje" buttonProps={addDataButtonProps}/>
      <div className="container-fluid px-4 py-3">
        <div className={itemsList.length > 0 ? "card shadow p-1 mb-5 bg-white rounded-2" : "card shadow mb-5 bg-transparent rounded-2"}>
          {loading ? <LoadingDataDiv/> : itemsList.length > 0 ? (
            <Table
              rows={itemsList}
              columns={columns}
              state={state}
              checkRelatedItems={() => setAllowDelete(true)}
              formHeader="Edycja lokalizacji"
              relatedItemsUrl={null}
              getCompleteItem={getCompleteItem}
            />
          ) : (
            <div className="warn_info">
              <FontAwesomeIcon className="warning-icon div-visible" icon={faExclamationCircle}/>&nbsp;&nbsp;&nbsp;
              NIE ZNALEZIONO ŻADNYCH LOKALIZACJI. DODAJ PIERWSZĄ LOKALIZACJĘ.
            </div>
          )}
        </div>
      </div>

      <SettingsEntityModal
        show={currentFormState.showForm}
        onClose={onClose}
        title={currentFormState.formAddingDataMode ? "Nowa pozycja: Lokalizacja" : `Edycja: ${currentItem.name || "Lokalizacja"}`}
        adding={currentFormState.formAddingDataMode}
        introText="Lokalizacja opisuje fizyczne lub biznesowe miejsce pracy, np. Słodka Chmurka, Labirynt Luster albo Kasy główne. Nie jest tym samym co stanowisko."
        onRestore={() => { setUpdateConflict(false); restoreFormData({state}); }}
        onDelete={onDelete}
        onSubmit={onSaveItemClick}
        state={state}
      >
        <form className="settings-form-content-card">
          <section className="settings-form-section">
            <div className="settings-form-section-heading">
              <div>
                <span className="settings-form-section-kicker">Miejsce wykonywania pracy</span>
                <h6>Lokalizacja</h6>
              </div>
            </div>
            <div className="settings-form-grid">
              <div className="settings-form-field">
                <label htmlFor="name">Nazwa *</label>
                <TextInput propertyName="name" required={true} state={state}/>
                <small>Pełna nazwa widoczna w grafiku, terminalach RCP i raportach SPMH.</small>
              </div>
              <div className="settings-form-field settings-form-field-short">
                <label htmlFor="shortName">Skrót *</label>
                <TextInput propertyName="shortName" required={true} state={state}/>
                <small>Krótki kod lokalizacji.</small>
              </div>
              <div className="settings-form-field settings-form-field-full">
                <label htmlFor="description">Opis</label>
                <TextArea propertyName="description" required={false} rows="3" state={state}/>
                <small>Opcjonalny opis punktu, strefy sprzedażowej albo miejsca pracy.</small>
              </div>
            </div>
          </section>
          {updateConflict && (
            <div className="dictionary-form-error">
              <FontAwesomeIcon icon={faInfoCircle}/>
              <span>Istnieje już lokalizacja o tej nazwie lub skrócie.</span>
            </div>
          )}
        </form>
      </SettingsEntityModal>
      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  );
};

export default Location;
